import { ArrowLeft, Loader2 } from 'lucide-react'
import { type ChangeEvent, type FormEvent, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { SplitScreenAuthLayout } from '@/components/layout/SplitScreenAuthLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { routePaths } from '@/lib/router'
import { authService } from '@/services/auth'

export function ForgotPasswordPage() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [error, setError] = useState('')

  const validateEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    if (!email.trim()) {
      setError('Email address is required')
      return
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address')
      return
    }

    setIsLoading(true)
    setError('')

    try {
      await authService.requestPasswordReset({
        email: email.trim().toLowerCase(),
      })

      setIsSuccess(true)

      toast.success('Reset Link Sent', {
        description: 'Check your email for password reset instructions.',
      })
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to send reset email. Please try again.'

      toast.error('Reset Failed', {
        description: errorMessage,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
    if (error) {
      setError('')
    }
  }

  const handleBackToLogin = () => {
    navigate(routePaths.login)
  }

  if (isSuccess) {
    return (
      <SplitScreenAuthLayout>
        <div className="space-y-12">
          {/* Header */}
          <div className="space-y-3 text-left">
            <h1 className="font-medium text-[30px] leading-[36px] text-foreground">
              Check Your Email
            </h1>
            <p className="text-[14px] leading-[20px] text-muted-foreground">
              We've sent a password reset link to <strong>{email}</strong>.
              Please check your inbox and follow the instructions to reset your
              password.
            </p>
          </div>

          {/* Info Box */}
          <div className="bg-secondary rounded-2xl p-4 text-[14px] text-muted-foreground">
            <p className="font-medium mb-2">Didn't receive the email?</p>
            <ul className="space-y-1 text-left">
              <li>• Check your spam/junk folder</li>
              <li>• Make sure {email} is correct</li>
              <li>• The email may take a few minutes to arrive</li>
              <li>• You can request another reset link if needed</li>
            </ul>
          </div>

          {/* Buttons */}
          <div className="space-y-3">
            <Button
              variant="outline"
              onClick={() => window.open('mailto:', '_blank')}
              className="w-full"
              size="lg"
            >
              Open Email App
            </Button>
            <Button onClick={handleBackToLogin} className="w-full" size="lg">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Login
            </Button>
          </div>

          {/* Footer Link */}
          <div className="text-center text-[14px]">
            <span className="text-muted-foreground">Need help? </span>
            <Button
              variant="link"
              className="p-0 h-auto text-blue-600 underline font-medium"
            >
              Contact Support
            </Button>
          </div>
        </div>
      </SplitScreenAuthLayout>
    )
  }

  return (
    <SplitScreenAuthLayout>
      <div className="space-y-12">
        {/* Header */}
        <div className="space-y-3 text-left">
          <h1 className="font-medium text-[30px] leading-[36px] text-foreground">
            Reset your password
          </h1>
          <p className="text-[14px] leading-[20px] text-muted-foreground">
            Remember your password?{' '}
            <Link to={routePaths.login} className="text-blue-600">
              Sign in
            </Link>
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex flex-col gap-2">
            <label
              htmlFor="email"
              className="text-[14px] font-medium text-foreground"
            >
              Email Address
            </label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={handleEmailChange}
              placeholder="Enter your email address"
              disabled={isLoading}
              className="h-9 rounded-full border-border bg-background px-3 py-1 text-[14px] shadow-sm focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:outline-none transition-[color,box-shadow]"
              autoFocus
            />
            {error && <p className="text-[14px] text-red-500">{error}</p>}
          </div>

          <Button
            type="submit"
            className="w-full"
            size="lg"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              'Send Reset Link'
            )}
          </Button>

          <Button
            type="button"
            variant="outline"
            onClick={handleBackToLogin}
            className="w-full"
            size="lg"
            disabled={isLoading}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Login
          </Button>
        </form>
      </div>
    </SplitScreenAuthLayout>
  )
}
